import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import Swal from 'sweetalert2';
import { BranchesService } from 'src/app/services/branches.service';
import { HttpService } from 'src/app/services/http-service.service';
import { AlertsService } from 'src/app/services/alerts.service';
import { GoogleMapsLoaderService } from 'src/app/services/google-maps-loader.service';
import { lastValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-sucursal-card',
  templateUrl: './sucursal-card.component.html',
  styleUrl: './sucursal-card.component.scss',
})
export class SucursalCardComponent implements OnInit {
  @ViewChild('map') mapElement: any;
  map: google.maps.Map | undefined;
  marker: google.maps.marker.AdvancedMarkerElement | undefined;

  public sucursal: any = null;
  public loading: boolean = true;
  private mapRequestId = 0;

  constructor(
    private readonly _branchService: BranchesService,
    private readonly _httpService: HttpService,
    private readonly _alert: AlertsService,
    private readonly _translate: TranslateService,
    private readonly googleMapsLoader: GoogleMapsLoaderService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.loadUserBranch();
  }

  async loadUserBranch() {
    try {
      const currentUser = JSON.parse(localStorage.getItem('current') || '{}');

      if (currentUser?.id) {
        try {
          const userBranchesResponse: any = await lastValueFrom(
            this._httpService.getUserBranches(currentUser.id)
          );

          if (
            userBranchesResponse?.success &&
            userBranchesResponse.data?.items?.length > 0
          ) {
            this.sucursal = userBranchesResponse.data.items[0];
            
            const branchInStorage = localStorage.getItem('branch');
            if (!branchInStorage) {
              this._branchService.sucursalId = this.sucursal.id;
              localStorage.setItem('branchId', this.sucursal.id.toString());
              localStorage.setItem('branch', JSON.stringify(this.sucursal));
            }

            this.loading = false;
            this.setMap();
            return;
          }
        } catch (error) {
          console.error('Error al obtener sucursal del usuario desde BD:', error);
        }
      }

      const branchInStorage = localStorage.getItem('branch');
      if (branchInStorage) {
        const parsedBranch = JSON.parse(branchInStorage);
        if (parsedBranch?.id && parsedBranch?.branchName) {
          try {
            const branches = await lastValueFrom(this._branchService.getAll());
            const freshBranch =
              branches.find((branch: any) => branch.id === parsedBranch.id) ??
              parsedBranch;
            this.sucursal = freshBranch;
            this._branchService.sucursalId = freshBranch.id;
            localStorage.setItem('branchId', freshBranch.id.toString());
            localStorage.setItem('branch', JSON.stringify(freshBranch));
            this.loading = false;
            this.setMap();
            return;
          } catch (error) {
            console.error('Error al refrescar sucursal desde cache:', error);
            this.sucursal = parsedBranch;
            this._branchService.sucursalId = parsedBranch.id;
            this.loading = false;
            this.setMap();
            return;
          }
        }
      }

      this._branchService.getAll().subscribe({
        next: (branches: any) => {
          if (branches && branches.length > 0) {
            this.sucursal = branches[0];
            this._branchService.sucursalId = this.sucursal.id;

            const branchInStorage = localStorage.getItem('branch');
            if (!branchInStorage) {
              localStorage.setItem('branchId', this.sucursal.id.toString());
              localStorage.setItem('branch', JSON.stringify(this.sucursal));
            }

            this.setMap();
          }
        },
        complete: () => {
          this.loading = false;
        },
      });
    } catch (error) {
      console.error('Error al cargar sucursal del usuario:', error);
      this.loading = false;
    }
  }

  async setMap(): Promise<void> {
    const { latitude, longitude } = this.sucursal.address;

    if (!latitude || !longitude) {
      console.error('Sucursal sin coordenadas válidas');
      return;
    }

    const requestId = ++this.mapRequestId;
    this.cdr.detectChanges();

    const element = this.mapElement?.nativeElement as HTMLElement | undefined;
    if (!element) {
      return;
    }

    try {
      if (this.marker) {
        this.marker.map = null;
        this.marker = undefined;
      }

      const { map, marker } = await this.googleMapsLoader.createBranchMap({
        element,
        latitude: Number(latitude),
        longitude: Number(longitude),
      });

      if (requestId !== this.mapRequestId) {
        marker.map = null;
        return;
      }

      this.map = map;
      this.marker = marker;
    } catch (error) {
      console.error('Error al inicializar el mapa de sucursal:', error);
    }
  }

  async selectSucursal() {
    let sucursales: any = [];

    this._branchService.getAll().subscribe((branches: any) => {
      sucursales = branches;
    });

    const currentBranchId = localStorage.getItem('branchId');
    let currentBranchIndex = '0';
    if (currentBranchId) {
      const index = sucursales.findIndex(
        (s: any) => s.id === parseInt(currentBranchId)
      );
      if (index !== -1) {
        currentBranchIndex = index.toString();
      }
    }

    const { value: sucursal } = await Swal.fire({
      title: this._translate.instant('PROFILE_PAGE.SWAL_CHANGE_BRANCH_TITLE'),
      text: this._translate.instant('PROFILE_PAGE.SWAL_CHANGE_BRANCH_TEXT'),
      input: 'select',
      inputOptions: sucursales.map((sucursal: any) => sucursal.branchName),
      inputValue: currentBranchIndex,
      inputPlaceholder: this._translate.instant(
        'PROFILE_PAGE.SWAL_BRANCH_PLACEHOLDER',
      ),
      showCancelButton: true,
      background: '#0B2634',
      color: '#fff',
      confirmButtonColor: '#00C08B',
      confirmButtonText: this._translate.instant(
        'PROFILE_PAGE.SWAL_BUTTON_SAVE',
      ),
      cancelButtonText: this._translate.instant('MODAL.BUTTON_CANCEL'),
      customClass: {
        input: 'input-class',
        inputLabel: 'input-label',
      },
      inputValidator: (value) => {
        return new Promise((resolve) => {
          resolve();
        });
      },
    });

    if (sucursal !== undefined && sucursal !== null) {
      const selectedBranch = sucursales[sucursal];
      const currentUser = JSON.parse(localStorage.getItem('current') || '{}');

      if (!currentUser?.id) {
        this._alert.show({
          title: this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
          text: this._translate.instant('PROFILE_PAGE.USER_SESSION_ERROR'),
          icon: 'error',
        });
        return;
      }

      try {
        const response: any = await lastValueFrom(
          this._httpService.assignUserBranch(currentUser.id, selectedBranch.id)
        );

        if (response?.success) {
          this._branchService.sucursalId = selectedBranch.id;
          this.sucursal = selectedBranch;
          localStorage.setItem('branchId', selectedBranch.id.toString());
          localStorage.setItem('branch', JSON.stringify(selectedBranch));
          this.setMap();

          this._alert.show({
            title: this._translate.instant(
              'PROFILE_PAGE.BRANCH_UPDATE_SUCCESS_TITLE',
            ),
            text:
              response.data?.items?.message ||
              response.message ||
              this._translate.instant(
                'PROFILE_PAGE.BRANCH_UPDATE_SUCCESS_TEXT',
              ),
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          const errorMessage =
            response?.error?.message ||
            response?.message ||
            this._translate.instant('PROFILE_PAGE.BRANCH_UPDATE_FAILED');

          this._alert.show({
            title:
              response?.message ||
              this._translate.instant('PROFILE_PAGE.GENERIC_ERROR'),
            text: errorMessage,
            icon: 'error',
          });
        }
      } catch (error: any) {
        console.error('Error al asignar sucursal:', error);

        let errorMessage = this._translate.instant(
          'PROFILE_PAGE.BRANCH_UPDATE_FAILED',
        );
        let errorTitle = this._translate.instant('PROFILE_PAGE.GENERIC_ERROR');

        if (error?.error) {
          errorTitle =
            error.error.message ||
            error.error.title ||
            this._translate.instant('PROFILE_PAGE.GENERIC_ERROR');
          errorMessage =
            error.error.error?.message || error.error.message || errorMessage;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this._alert.show({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
        });
      }
    }
  }
}
